import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Key, Activity, Zap, Calendar, 
  LogOut, ChevronRight, AlertCircle, CheckCircle2,
  BarChart3, Shield, Copy, Check
} from 'lucide-react';
import BloodFlowLogo from '../../Assets/BloodflowLogoNoLetters.png';

// Particle background effect
const ParticleField = () => {
  const particles = useMemo(() => 
    [...Array(30)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 2,
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-red-500"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            opacity: [0, 0.4, 0],
            scale: [0, 1, 0],
            y: [0, -80],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated stat card
const StatCard = ({ icon: Icon, title, value, subtitle, delay, color = 'red' }) => {
  const colorClasses = {
    red: 'from-red-500/20 to-red-600/5 border-red-500/20 hover:border-red-500/40',
    green: 'from-green-500/20 to-green-600/5 border-green-500/20 hover:border-green-500/40',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/20 hover:border-amber-500/40',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/20 hover:border-blue-500/40',
  };

  const iconColors = {
    red: 'text-red-400 bg-red-500/10',
    green: 'text-green-400 bg-green-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: "easeOut" }}
      className={`relative group bg-gradient-to-br ${colorClasses[color]} backdrop-blur-xl rounded-2xl p-6 border transition-all duration-300`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <h3 className="text-gray-400 text-sm font-medium mb-1">{title}</h3>
      <p className="text-white text-2xl font-bold tracking-tight">{value}</p>
      {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
    </motion.div>
  );
};

// Progress ring component
const ProgressRing = ({ percentage, size = 120, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage > 80) return '#ef4444';
    if (percentage > 50) return '#f59e0b';
    return '#22c55e';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-800"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <motion.circle
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white">{percentage}%</span>
        <span className="text-xs text-gray-400">used</span>
      </div>
    </div>
  );
};

const ApiKeyDashboard = () => {
  const [apiKey, setApiKey] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [copied, setCopied] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bloodflow.eu';

  const fetchDashboardData = useCallback(async (key) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/keys/dashboard`, {
        method: 'GET',
        headers: {
          'X-API-Key': key,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid API key. Please check and try again.');
        }
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
      setIsAuthenticated(true);
      sessionStorage.setItem('bf_api_key', key);
    } catch (err) {
      setError(err.message);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const storedKey = sessionStorage.getItem('bf_api_key');
    if (storedKey) {
      setApiKey(storedKey);
      fetchDashboardData(storedKey);
    }
  }, [fetchDashboardData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (apiKey.trim()) {
      fetchDashboardData(apiKey.trim());
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('bf_api_key');
    setApiKey('');
    setDashboardData(null);
    setIsAuthenticated(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatNumber = (num, decimals = 1) => {
    if (num == null || isNaN(num)) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(decimals) + 'M';
    if (num >= 10000) return (num / 1000).toFixed(decimals) + 'K';
    if (num >= 1000) return (num / 1000).toFixed(decimals) + 'K';
    return num.toFixed(decimals);
  };

  const formatPercentage = (used, total) => {
    if (!total || total === 0) return '0';
    const percent = (used / total) * 100;
    if (percent < 0.1 && percent > 0) return '<0.1';
    if (percent < 1) return percent.toFixed(1);
    return Math.round(percent).toString();
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden font-sans">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-[120px]" />
        
        <ParticleField />

        <div className="relative min-h-screen flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="w-full max-w-md"
          >
            {/* Logo */}
            <motion.div 
              className="flex justify-center mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <img src={BloodFlowLogo} alt="BloodFlow" className="h-20 w-auto" />
            </motion.div>

            {/* Card */}
            <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-white/5 shadow-2xl">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">API Dashboard</h1>
                <p className="text-gray-400 text-sm">Enter your API key to view usage and analytics</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="apiKey" className="block text-sm font-medium text-gray-300 mb-2">
                    API Key
                  </label>
                  <div className="relative">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="password"
                      id="apiKey"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="bf_xxxxxxxxxxxxxxxx"
                      className="w-full bg-black/50 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 font-mono text-sm focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 transition-all"
                      autoComplete="off"
                    />
                  </div>
                </div>
                
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4"
                    >
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-red-300 text-sm">{error}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <motion.button 
                  type="submit"
                  disabled={isLoading || !apiKey.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
                >
                  {isLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Access Dashboard
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </form>
              
              <div className="mt-8 pt-6 border-t border-white/5 text-center">
                <p className="text-gray-500 text-sm">
                  Need an API key?{' '}
                  <a href="/contact-sales" className="text-red-400 hover:text-red-300 transition-colors">
                    Contact Sales
                  </a>
                </p>
              </div>
            </div>

            {/* Security badge */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-2 mt-6 text-gray-500 text-xs"
            >
              <Shield className="w-4 h-4" />
              Secure connection • End-to-end encrypted
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Loading State
  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-sans">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-3 border-red-500/30 border-t-red-500 rounded-full mx-auto mb-4"
            style={{ borderWidth: '3px' }}
          />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const { apiKey: keyInfo, usage, statistics, recentRequests } = dashboardData || {};

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]" />
      <div className="fixed bottom-0 left-0 w-[400px] h-[400px] bg-red-600/5 rounded-full blur-[120px]" />

      <div className="relative">
        {/* Header */}
        <header className="border-b border-white/5 bg-black/50 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <img src={BloodFlowLogo} alt="BloodFlow" className="h-24 w-auto" />
              </div>
              
              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-2 bg-white/5 rounded-lg px-4 py-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-300">{keyInfo?.name}</span>
                </div>
                <motion.button 
                  onClick={handleLogout}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-all text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </motion.button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, <span className="text-red-400">{keyInfo?.name}</span>
            </h1>
            <p className="text-gray-400">Monitor your API usage and performance metrics</p>
          </motion.div>

          {/* API Key Display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 bg-gradient-to-r from-gray-900/80 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Your API Key</p>
                <div className="flex items-center gap-3">
                  <code className="text-lg font-mono text-white">{keyInfo?.maskedKey}</code>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    keyInfo?.isActive 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {keyInfo?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => copyToClipboard(keyInfo?.maskedKey)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 transition-all text-sm"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </motion.button>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Activity}
              title="Token Usage"
              value={`${formatNumber(usage?.tokensUsed || 0, 1)} / ${formatNumber(usage?.tokenLimit || 0, 0)}`}
              subtitle={`${formatPercentage(usage?.tokensUsed, usage?.tokenLimit)}% of limit used`}
              delay={0.1}
              color="red"
            />
            <StatCard
              icon={Zap}
              title="Tokens Remaining"
              value={formatNumber(usage?.tokensRemaining || 0, 0)}
              subtitle="Available to use"
              delay={0.2}
              color={usage?.tokensRemaining > 1000 ? 'green' : 'amber'}
            />
            <StatCard
              icon={BarChart3}
              title="API Requests"
              value={statistics?.last30Days?.totalRequests || 0}
              subtitle="Last 30 days"
              delay={0.3}
              color="blue"
            />
            <StatCard
              icon={Calendar}
              title="Last Reset"
              value={usage?.lastResetDate ? format(new Date(usage.lastResetDate), 'MMM d') : 'N/A'}
              subtitle="Token reset date"
              delay={0.4}
              color="amber"
            />
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Usage Progress Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-6">Usage Overview</h3>
              <div className="flex flex-col items-center">
                <ProgressRing 
                  percentage={usage?.tokenLimit ? Math.round((usage.tokensUsed / usage.tokenLimit) * 100) : 0} 
                  size={160} 
                  strokeWidth={12} 
                />
                <div className="mt-6 w-full space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Used</span>
                    <span className="text-white font-medium">{formatNumber(usage?.tokensUsed || 0, 1)} tokens</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Remaining</span>
                    <span className="text-white font-medium">{formatNumber(usage?.tokensRemaining || 0, 0)} tokens</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Total Limit</span>
                    <span className="text-white font-medium">{formatNumber(usage?.tokenLimit || 0, 0)} tokens</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Usage Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2 bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Usage Over Time</h3>
                <span className="text-xs text-gray-500">Last 30 days</span>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={statistics?.usageByDay || []}>
                  <defs>
                    <linearGradient id="tokenGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                    stroke="#4b5563"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#4b5563"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'MMMM d, yyyy')}
                    contentStyle={{ 
                      backgroundColor: '#111827', 
                      border: '1px solid rgba(255,255,255,0.1)', 
                      borderRadius: '12px',
                      color: '#fff',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="tokens" 
                    stroke="#ef4444" 
                    strokeWidth={2}
                    fill="url(#tokenGradient)"
                    name="Tokens Used"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          </div>

          {/* Daily Requests Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Daily API Requests</h3>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-3 h-3 rounded bg-red-500" />
                Requests
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statistics?.usageByDay || []}>
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => format(new Date(date), 'MM/dd')}
                  stroke="#4b5563"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#4b5563"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  labelFormatter={(date) => format(new Date(date), 'MMMM d, yyyy')}
                  contentStyle={{ 
                    backgroundColor: '#111827', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '12px',
                    color: '#fff'
                  }}
                />
                <Bar 
                  dataKey="requests" 
                  fill="#ef4444" 
                  name="Requests" 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Recent Requests Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden"
          >
            <div className="p-6 border-b border-white/5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Recent API Requests</h3>
                <span className="text-xs text-gray-500">Last 20 requests</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Timestamp</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Endpoint</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Tokens</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {recentRequests?.length > 0 ? (
                    recentRequests.map((request, index) => (
                      <motion.tr 
                        key={request.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.03 }}
                        className="hover:bg-white/5 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {format(new Date(request.timestamp), 'MMM d, HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm text-red-400 bg-red-500/10 px-2 py-1 rounded">
                            {request.endpoint}
                          </code>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300 text-right font-mono">
                          {request.tokensConsumed.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            Success
                          </span>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        No recent requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Footer Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-center text-gray-500 text-sm"
          >
            <p>
              API key created on {keyInfo?.createdAt ? format(new Date(keyInfo.createdAt), 'MMMM d, yyyy') : 'N/A'}
              {keyInfo?.description && ` • ${keyInfo.description}`}
            </p>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ApiKeyDashboard;
